import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchWithRetry } from '../../util/sliceUtils';
import config from '../../config';
import i18n from '../../i18n';

const backendUrl = config.backendUrl;
const TRAFFICAMOUNT_URL = backendUrl + '/v1/trafficAmount';
const CONGESTION_URL = backendUrl + '/v1/congestion';
const QUEUELENGTH_URL = backendUrl + '/v1/queueLength';
const DEVICESTATE_URL = backendUrl + '/v1/deviceState';
const DEVICE_PREFIX = 'tre';

// Cache for resolved device redirects (e.g. 602 -> 601) to avoid repeated 404s
let resolvedDeviceMapping = {};

export const resetResolvedDeviceMapping = () => {
  resolvedDeviceMapping = {};
};

export const deviceStateSlice = createSlice({
  name: 'deviceState',
  initialState: {
    trafficAmount: {},
    congestion: {},
    queueLength: {},
    deviceState: {},
    trafficAmountLoading: false,
    congestionLoading: false,
    queueLengthLoading: false,
    deviceStateLoading: false,
    deviceStateError: null,
    deviceStateRetryCount: 0,
  },
  reducers: {
    setTrafficAmount: (state, action) => {
      state.trafficAmount = action.payload;
    },
    setCongestion: (state, action) => {
      state.congestion = action.payload;
    },
    setQueueLength: (state, action) => {
      state.queueLength = action.payload;
    },
    setDeviceState: (state, action) => {
      state.deviceState = action.payload;
      state.deviceStateError = null;
      state.deviceStateRetryCount = 0;
    },
    setTrafficAmountLoading: (state, action) => {
      state.trafficAmountLoading = action.payload;
    },
    setCongestionLoading: (state, action) => {
      state.congestionLoading = action.payload;
    },
    setQueueLengthLoading: (state, action) => {
      state.queueLengthLoading = action.payload;
    },
    setDeviceStateLoading: (state, action) => {
      state.deviceStateLoading = action.payload;
    },
    setDeviceStateError: (state, action) => {
      state.deviceStateError = action.payload;
    },
    incrementDeviceStateRetryCount: (state) => {
      state.deviceStateRetryCount += 1;
    },
    resetDeviceStateRetryCount: (state) => {
      state.deviceStateRetryCount = 0;
    },
  },
});

export const {
  setTrafficAmount, setCongestion, setQueueLength, setDeviceState,
  setTrafficAmountLoading, setCongestionLoading, setQueueLengthLoading, setDeviceStateLoading,
  setDeviceStateError, incrementDeviceStateRetryCount, resetDeviceStateRetryCount
} = deviceStateSlice.actions;

// Store last device name for retry
let lastDeviceName = null;

// Retry thunk for manual retry from toast - this is what gets dispatched when clicking retry
export const retryDeviceState = () => (dispatch) => {
  console.log('Manually retrying device state...');
  dispatch(resetDeviceStateRetryCount());
  if (lastDeviceName) {
    dispatch(getDeviceState(lastDeviceName));
  }
};

export const getTrafficAmount = (deviceName, detectorName, startTime, endTime) => dispatch => {
  const pathParameters = getPathParameters(deviceName, detectorName);
  const queryParameters = getQueryParameters(startTime, endTime);
  const config = {
    headers: {}
  };
  dispatch(setTrafficAmountLoading(true));
  const url = `${TRAFFICAMOUNT_URL}${pathParameters}${queryParameters}`;
  axios.get(url, config).then(
    response => {
      dispatch(setTrafficAmount(response.data));
      dispatch(setTrafficAmountLoading(false));
    },
    error => {
      console.log("Intersections error", error);
      dispatch(setTrafficAmountLoading(false));
    }
  );
};

export const getCongestion = (deviceName, detectorName, startTime, endTime) => dispatch => {
  const pathParameters = getPathParameters(deviceName, detectorName);
  const queryParameters = getQueryParameters(startTime, endTime);
  const config = {
    headers: {}
  };
  dispatch(setCongestionLoading(true));
  const url = `${CONGESTION_URL}${pathParameters}${queryParameters}`;
  axios.get(url, config).then(
    response => {
      dispatch(setCongestion(response.data));
      dispatch(setCongestionLoading(false));
    },
    error => {
      console.log("Congestion error", error);
      dispatch(setCongestionLoading(false));
    }
  );
};

export const getQueueLength = (deviceName, detectorName, startTime, endTime) => dispatch => {
  const pathParameters = getPathParameters(deviceName, detectorName);
  const queryParameters = getQueryParameters(startTime, endTime);
  const config = {
    headers: {}
  };
  dispatch(setQueueLengthLoading(true));
  const url = `${QUEUELENGTH_URL}${pathParameters}${queryParameters}`;
  axios.get(url, config).then(
    response => {
      dispatch(setQueueLength(response.data));
      dispatch(setQueueLengthLoading(false));
    },
    error => {
      console.log("QueueLength error", error);
      dispatch(setQueueLengthLoading(false));
    }
  );
};

export const getDeviceState = (deviceName, startTime, endTime, forceRetry = false) => (dispatch, getState) => {
  // Store device name for potential manual retry
  lastDeviceName = deviceName;

  // Check cache first
  const cachedTarget = resolvedDeviceMapping[deviceName];
  if (cachedTarget) {
    const pathParameters = getPathParameters(cachedTarget, null);
    const queryParameters = getQueryParameters(startTime, endTime);
    const url = `${DEVICESTATE_URL}${pathParameters}${queryParameters}`;

    return dispatch(fetchWithRetry({
      fetchFn: () => axios.get(url, { headers: {} }),
      actions: {
        setData: setDeviceState,
        setLoading: setDeviceStateLoading,
        setError: setDeviceStateError,
        incrementRetry: incrementDeviceStateRetryCount,
      },
      selectors: {
        selectRetryCount: selectDeviceStateRetryCount,
        selectLoading: selectDeviceStateLoading,
      },
      retryActionName: 'RETRY_DEVICE_STATE',
      errorMessagePrefix: i18n.t('error.device_state', { device: `${DEVICE_PREFIX}${deviceName}` }),
      retryThunk: (force) => getDeviceState(deviceName, startTime, endTime, force),
      shouldRetry: (state) => checkShouldRetry(state, deviceName)
    }, forceRetry));
  }

  const state = getState();
  const intersections = state.trafficInfra?.intersections?.features || [];
  const intersection = intersections.find(f =>
    String(f.properties?.liva_nro).toLowerCase() === String(deviceName).toLowerCase() ||
    String(f.properties?.liva).toLowerCase() === String(deviceName).toLowerCase()
  );


  // Identify potential fallback candidates from yhteinen_koje

  // Identify potential fallback candidates from yhteinen_koje
  let fallbackCandidates = [];
  if (intersection && intersection.properties?.yhteinen_koje) {
    const rawYhteinen = intersection.properties.yhteinen_koje;
    // Parse comma-separated values, remove (...) notes, and trim
    const candidates = rawYhteinen.split(',').map(s => s.replace(/\([^)]*\)/g, '').trim());
    // Filter out self-reference and empty strings
    fallbackCandidates = candidates.filter(c => c && c.toLowerCase() !== String(deviceName).toLowerCase());
  }

  const queryParameters = getQueryParameters(startTime, endTime);

  // Custom fetch function that tries primary first, then fallback
  const smartFetchFn = async () => {
    const primaryPath = getPathParameters(deviceName, null);
    const primaryUrl = `${DEVICESTATE_URL}${primaryPath}${queryParameters}`;

    try {
      return await axios.get(primaryUrl, { headers: {} });
    } catch (error) {
      if (fallbackCandidates.length > 0) {
        // Try candidates in order
        for (const candidate of fallbackCandidates) {
          try {
            const fallbackPath = getPathParameters(candidate, null);
            const fallbackUrl = `${DEVICESTATE_URL}${fallbackPath}${queryParameters}`;
            const response = await axios.get(fallbackUrl, { headers: {} });

            // Cache the successful redirection
            resolvedDeviceMapping[deviceName] = candidate;

            return response;
          } catch (fallbackError) {
            console.warn(`Fallback fetch for ${candidate} failed.`, fallbackError);
            // Continue to next candidate
          }
        }
      }
      // If all fallbacks fail (or no candidates), throw the original error
      throw error;
    }
  };

  return dispatch(fetchWithRetry({
    fetchFn: smartFetchFn,
    actions: {
      setData: setDeviceState,
      setLoading: setDeviceStateLoading,
      setError: setDeviceStateError,
      incrementRetry: incrementDeviceStateRetryCount,
    },
    selectors: {
      selectRetryCount: selectDeviceStateRetryCount,
      selectLoading: selectDeviceStateLoading,
    },
    retryActionName: 'RETRY_DEVICE_STATE',
    errorMessagePrefix: i18n.t('error.device_state', { device: `${DEVICE_PREFIX}${deviceName}` }), // Keep original name in error
    retryThunk: (force) => getDeviceState(deviceName, startTime, endTime, force), // Retry with original name
    shouldRetry: (state) => {
      const { activeLight } = state.map;

      const activeId = activeLight ? String(activeLight.liva_nro).toLowerCase() : 'null';
      const targetId = String(deviceName).toLowerCase(); // Check against original requested ID

      if (!activeLight || !activeLight.liva_nro) return false;

      // Normalize IDs for comparison (handle potential 'tre' prefix differences)
      const targetIdWithPrefix = (DEVICE_PREFIX + targetId).toLowerCase();

      // Check if active matches target (with or without prefix)
      return activeId === targetId || activeId === targetIdWithPrefix || activeId.endsWith(targetId);
    }
  }, forceRetry));
};


export const selectTrafficAmount = state => state.deviceState.trafficAmount;
export const selectCongestion = state => state.deviceState.congestion;
export const selectQueueLength = state => state.deviceState.queueLength;
export const selectDeviceState = state => state.deviceState.deviceState;

export const selectTrafficAmountLoading = state => state.deviceState.trafficAmountLoading;
export const selectCongestionLoading = state => state.deviceState.congestionLoading;
export const selectQueueLengthLoading = state => state.deviceState.queueLengthLoading;
export const selectDeviceStateLoading = state => state.deviceState.deviceStateLoading;
export const selectDeviceStateError = state => state.deviceState.deviceStateError;
export const selectDeviceStateRetryCount = state => state.deviceState.deviceStateRetryCount;

export default deviceStateSlice.reducer;

const getPathParameters = (deviceName, detectorName) => {
  return deviceName && detectorName ? `/${DEVICE_PREFIX}${deviceName}/${detectorName}` :
    deviceName ? `/${DEVICE_PREFIX}${deviceName}` : '';
};


const getQueryParameters = (startTime, endTime) => {
  if ((startTime || endTime) && !(startTime && endTime)) {
    console.error('Both startTime and endTime are needed if either is defined.');
    return '';
  }
  return startTime && endTime ? `?startTime=${startTime}&endTime=${endTime}` : '';
};
